// client/src/pages/Game/Battlefield/components/TurnTimerIndicator.jsx
//
// R34v : indicateur de tour TOUJOURS visible (tour joueur ET tour adverse).
//  - Contour JAUNE autour de la fenêtre, plein au début du tour, qui se
//    consomme sur 30 s : la disparition commence EN HAUT et progresse
//    vers la gauche jusqu'à disparaître pile à la fin des 30 secondes.
//  - Le compte à rebours chiffré n'apparaît QUE dans les 10 dernières
//    secondes (et passe au rouge).
//  - PvP (R30) : chrono serveur (turnDeadline), même comportement visuel.
//  - À zéro (tour joueur) : fin de tour automatique (onAutoEndTurn ;
//    en PvP le serveur gère, l'appel local n'est qu'un filet de sécurité).
//  - Masqué pendant le mulligan/RPS ou si un détail de carte est ouvert.
import React, { useEffect, useRef, useState } from 'react';
import './TurnTimerIndicator.css';

const TURN_MS = 30000;      // durée totale du tour
const COUNTDOWN_BELOW_MS = 10000; // chiffres visibles sous 10 s seulement

export default function TurnTimerIndicator({
  gameState,
  isHidden,       // ex : detail de carte ouvert
  onAutoEndTurn,  // appelé quand le contour est consommé (tour joueur)
  getPhaseDescription,
}) {
  const isPlayerTurn = gameState?.activePlayer === 'player';
  const inMainPhase = gameState && gameState.phase !== 'mulligan' && gameState.phase !== 'rps';

  // R30 : chrono serveur (PvP) — deadline absolue fournie par le serveur
  const serverDeadline = gameState?.turnDeadline || null;
  const serverDuration = gameState?.turnDurationMs || TURN_MS;

  const [progress, setProgress] = useState(1); // 1 -> 0 sur 30 s
  const rafRef = useRef(null);
  const endedRef = useRef(false);

  // Chrono local (duel IA) : redémarre à chaque changement de tour/phase.
  // La durée de référence est le début du tour actif — le contour se
  // consomme d'un bloc sur 30 s, indépendamment des actions du joueur.
  useEffect(() => {
    if (!serverDeadline && (!inMainPhase || !isPlayerTurn)) {
      setProgress(1);
      return undefined;
    }
    if (serverDeadline && (!inMainPhase || !isPlayerTurn)) return undefined;

    endedRef.current = false;
    const duration = serverDeadline ? serverDuration : TURN_MS;
    const t0 = serverDeadline
      ? Date.now() - (duration - Math.max(0, serverDeadline - Date.now()))
      : performance.now();
    const tick = () => {
      const now = serverDeadline ? Date.now() : performance.now();
      const p = Math.max(0, 1 - (now - t0) / duration);
      setProgress(p);
      if (p <= 0) {
        if (!endedRef.current) {
          endedRef.current = true;
          if (isPlayerTurn && onAutoEndTurn) onAutoEndTurn();
        }
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [serverDeadline, isPlayerTurn, gameState?.turn]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!inMainPhase || isHidden) return null;

  // Contour : plein au départ, il se consomme en partant du HAUT vers la
  // gauche (le dernier segment visible est le haut du bord gauche).
  const W = 100, H = 100, PER = 2 * (W + H);
  const dash = PER * progress;
  const msLeft = progress * (serverDeadline ? serverDuration : TURN_MS);
  const critical = msLeft <= COUNTDOWN_BELOW_MS;
  const showCount = isPlayerTurn && msLeft <= COUNTDOWN_BELOW_MS && msLeft > 0;

  return (
    <div
      className={`turn-timer-indicator always${critical && isPlayerTurn ? ' critical' : ''}${isPlayerTurn ? '' : ' opponent'}`}
    >
      {isPlayerTurn && (
        <svg className="turn-timer-border" viewBox="0 0 100 100" preserveAspectRatio="none">
          <rect
            x="0.6" y="0.6" width="98.8" height="98.8" rx="6"
            fill="none"
            stroke={critical ? '#ff5a5a' : '#ffd166'}
            strokeWidth="1.6"
            vectorEffect="non-scaling-stroke"
            strokeDasharray={`${dash} ${PER}`}
            strokeDashoffset={dash - PER}
            strokeLinecap="round"
          />
        </svg>
      )}
      <div className={`turn-timer-title${isPlayerTurn ? '' : ' opp'}`}>
        {isPlayerTurn ? 'Votre tour' : 'Tour de l\u2019adversaire'}
      </div>
      <div className="turn-timer-sub">
        (Tour {gameState.turn})
      </div>
      <div className={`turn-timer-attack ${gameState.playerHasAttack ? 'mine' : ''}`}>
        {gameState.playerHasAttack ? 'Vous avez l\u2019attaque' : 'Adversaire a l\u2019attaque'}
      </div>
      <div className="turn-timer-phase">
        {getPhaseDescription ? getPhaseDescription() : ''}
      </div>
      {showCount && (
        <div className={`turn-timer-count ${critical ? 'critical' : ''}`}>
          {Math.ceil(msLeft / 1000)}s
        </div>
      )}
    </div>
  );
}
